import React, { createContext, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { type ConditionProperties } from 'json-rules-engine';
import type jsep from 'jsep';
import { type Static } from 'runtypes';
import {
  Array,
  Boolean,
  Literal,
  Null,
  Number,
  Object,
  String,
  Tuple,
  Union,
} from 'runtypes';
import { BrightbackExpression } from '@bbkAdminUtils/brightback-expression';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import { type CreateRuleDto, type Rule } from '@bbkAdminRedux/rtkq/rules.slice';
import { rulesSlice } from '@bbkAdminRedux/rtkq/rules.slice';
import { type RuleTopLevelCondition } from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import {
  isAndGroup,
  isCondition,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/rules-utils';
import {
  type AudienceFact,
  NullishOperator,
} from '@bbkAdminComponents/experiences/audiences/edit-rule/condition-item';
import { validateSchema } from '@bbkAdminUtils/api-client/validateSchema';
import { type EnumLike } from '@bbkAdminUtils/utility-types';
import { type RTKAPIErrorResponse } from '@bbkAdminRedux/rtkq/rtkq-error-logger';

type ExpressionSimpleType = string | number | boolean | null;

const NodeLiteral = Object({
  type: Literal('Literal'),
  value: Union(String, Number, Boolean, Null),
});

type NodeLiteral = Static<typeof NodeLiteral>;

const NodeIdentifier = Object({
  type: Literal('Identifier'),
  name: String,
});

type NodeIdentifier = Static<typeof NodeIdentifier>;

const ValueCallSchema = Object({
  type: Literal('CallExpression'),
  callee: Object({
    type: Literal('Identifier'),
    name: Literal('value'),
  }),
  arguments: Tuple(
    Object({
      type: Literal('Literal'),
      value: String,
    })
  ),
});

type ValueCallSchema = Static<typeof ValueCallSchema>;

const isValueCall = (
  ast: TopLevelSchema | ValueCallSchema
): ast is ValueCallSchema => {
  return (
    ast.type === 'CallExpression' &&
    ast.callee.type === 'Identifier' &&
    ast.callee.name === 'value'
  );
};

const BinaryConditionSchema = Object({
  type: Literal('BinaryExpression'),
  left: ValueCallSchema,
  operator: Union(
    Literal('=='),
    Literal('!='),
    Literal('>'),
    Literal('>='),
    Literal('<'),
    Literal('<=')
  ),
  right: Union(NodeLiteral, NodeIdentifier),
});

type BinaryConditionSchema = Static<typeof BinaryConditionSchema>;

const isBinary = (
  node: TopLevelSchema | ConditionSchema
): node is BinaryConditionSchema => {
  return node.type === 'BinaryExpression';
};

const isLiteral = (
  node: BinaryConditionSchema['right']
): node is NodeLiteral => {
  return node.type === 'Literal';
};

const isIdentifier = (
  node: BinaryConditionSchema['right']
): node is NodeIdentifier => {
  return node.type === 'Identifier';
};

const GenericMethodsSchema = Object({
  type: Literal('CallExpression'),
  callee: Object({
    type: Literal('MemberExpression'),
    object: Object({
      type: Literal('Identifier'),
      name: Literal('Generic'),
    }),
    property: Object({
      type: Literal('Identifier'),
      name: Union(Literal('isNilOrEmpty'), Literal('isNotNilOrEmpty')),
    }),
  }),
  arguments: Tuple(ValueCallSchema),
});

const isGenericMethod = (
  ast: TopLevelSchema | ConditionSchema
): ast is GenericMethodsSchema => {
  return (
    ast.type === 'CallExpression' &&
    ast.callee.type === 'MemberExpression' &&
    ast.callee.object.type === 'Identifier' &&
    ast.callee.object.name === 'Generic'
  );
};

type GenericMethodsSchema = Static<typeof GenericMethodsSchema>;

const StringMethodsSchema = Object({
  type: Literal('CallExpression'),
  callee: Object({
    type: Literal('MemberExpression'),
    object: Object({
      type: Literal('Identifier'),
      name: Literal('String'),
    }),
    property: Object({
      type: Literal('Identifier'),
      name: Union(
        Literal('in'),
        Literal('notIn'),
        Literal('includes'),
        Literal('notIncludes'),
        Literal('startsWith')
      ),
    }),
  }),
  arguments: Array(Union(ValueCallSchema, NodeLiteral)),
});

const isStringMethod = (
  ast: TopLevelSchema | ConditionSchema
): ast is StringMethodsSchema => {
  return (
    ast.type === 'CallExpression' &&
    ast.callee.type === 'MemberExpression' &&
    ast.callee.object.type === 'Identifier' &&
    ast.callee.object.name === 'String'
  );
};

type StringMethodsSchema = Static<typeof StringMethodsSchema>;

const DateMethodsSchema = Object({
  type: Literal('CallExpression'),
  callee: Object({
    type: Literal('MemberExpression'),
    object: Object({
      type: Literal('Identifier'),
      name: Literal('Date'),
    }),
    property: Object({
      type: Literal('Identifier'),
      name: Union(
        Literal('isBefore'),
        Literal('isAfter'),
        Literal('willHappenWithinDays'),
        Literal('didHappenWithinDays')
      ),
    }),
  }),
  arguments: Tuple(ValueCallSchema, NodeLiteral),
});

const isDateMethod = (
  ast: TopLevelSchema | ConditionSchema
): ast is DateMethodsSchema => {
  return (
    ast.type === 'CallExpression' &&
    ast.callee.type === 'MemberExpression' &&
    ast.callee.object.type === 'Identifier' &&
    ast.callee.object.name === 'Date'
  );
};

type DateMethodsSchema = Static<typeof DateMethodsSchema>;

const AndOrSchema = Object({
  type: Literal('CallExpression'),
  callee: Object({
    type: Literal('MemberExpression'),
    object: Object({
      type: Literal('Identifier'),
      name: Literal('Boolean'),
    }),
    property: Object({
      type: Literal('Identifier'),
      name: Union(Literal('or'), Literal('and')),
    }),
  }),
});

type UtilSchema<
  T extends GenericMethodsSchema | StringMethodsSchema | DateMethodsSchema,
> = T['callee']['property']['name'];

const ConditionSchema = Union(
  BinaryConditionSchema,
  GenericMethodsSchema,
  StringMethodsSchema,
  DateMethodsSchema
);

type ConditionSchema = Static<typeof ConditionSchema>;

// top level group contains either and/or group or conditions
const TopLevelSchema = AndOrSchema.and(
  Object({
    arguments: Array(
      Union(
        ConditionSchema,
        AndOrSchema.and(
          Object({
            arguments: Array(ConditionSchema),
          })
        )
      )
    ),
  })
);

type TopLevelSchema = Static<typeof TopLevelSchema>;

export const validateAst = (ast: jsep.Expression): TopLevelSchema => {
  return validateSchema(TopLevelSchema, true)(ast) as TopLevelSchema;
};

// in most cases we map operators to exact one '==' to '==', etc
// but in case == null or == undefined we map to JRE operators is null and is undefined or is blank
const getJsonRulesEngineOperatorFromExpressionCondition = (
  condition: BinaryConditionSchema
): BinaryConditionSchema['operator'] | EnumLike<typeof NullishOperator> => {
  switch (true) {
    // TODO: Remove code that uses isNull, isUndefined, isBlank. It's been deprecated and should be removed
    case condition.operator === '==' &&
      isLiteral(condition.right) &&
      condition.right.value === null:
      return NullishOperator.isNull;
    case condition.operator === '==' &&
      isIdentifier(condition.right) &&
      condition.right.name === 'undefined':
      return NullishOperator.isUndefined;
    case condition.operator === '==' &&
      isLiteral(condition.right) &&
      condition.right.value === '':
      return NullishOperator.isBlank;
    default:
      return condition.operator;
  }
};

export const convertAstToJsonRulesEngine = (
  ast: TopLevelSchema | ConditionSchema
): RuleTopLevelCondition | ConditionProperties => {
  if (isBinary(ast)) {
    const operator = getJsonRulesEngineOperatorFromExpressionCondition(ast);
    return {
      fact: ast.left.arguments[0].value,
      operator,
      value: ast.right.type === 'Identifier' ? ast.right.name : ast.right.value,
    };
  }

  if (isGenericMethod(ast)) {
    const [valueCall] = ast.arguments;
    const fact = valueCall.arguments[0].value;
    const operator = ast.callee.property.name;
    const value = undefined;
    return {
      fact,
      operator: `Generic.${operator}`,
      value,
    };
  }

  if (isStringMethod(ast)) {
    const valueCall = ast.arguments[0]! as ValueCallSchema | TopLevelSchema;
    if (!isValueCall(valueCall)) {
      throw new Error('String methods should have first argument value call');
    }
    const [, ...arr] = ast.arguments;
    const literalArr = arr.filter((x) => x.type === 'Literal');
    const fact = valueCall.arguments[0].value;
    const operator = ast.callee.property.name;
    let value: ExpressionSimpleType | ExpressionSimpleType[];
    if (operator === 'in' || operator === 'notIn') {
      value = literalArr.map((x) => x.value);
    } else {
      value = literalArr[0]!.value;
    }
    return {
      fact,
      operator: `String.${operator}`,
      value,
    };
  }

  if (isDateMethod(ast)) {
    const [valueCall, arg] = ast.arguments;
    const fact = valueCall.arguments[0].value;
    const operator = ast.callee.property.name;
    const value = arg.value;
    return {
      fact,
      operator: `Date.${operator}`,
      value,
    };
  }

  const union = ast.callee.property.name === 'and' ? 'all' : 'any';
  const conditions = ast.arguments.map(convertAstToJsonRulesEngine);
  return { [union]: conditions } as unknown as RuleTopLevelCondition;
};

type GlobalOperator =
  | BinaryConditionSchema['operator']
  | EnumLike<typeof NullishOperator>;

const getExpressionConditionFromJsonRulesEngineCondition = (
  condition: ConditionProperties
): Pick<BinaryConditionSchema, 'right'> & { operator: GlobalOperator } => {
  switch (true) {
    // TODO: Remove code that uses isNull, isUndefined, isBlank. It's been deprecated and should be removed
    case condition.operator === NullishOperator.isNull:
      return {
        operator: '==',
        right: { type: 'Literal', value: null },
      };
    case condition.operator === NullishOperator.isUndefined:
      return {
        operator: '==',
        right: { type: 'Identifier', name: 'undefined' },
      };
    case condition.operator === NullishOperator.isBlank:
      return {
        operator: '==',
        right: { type: 'Literal', value: '' },
      };
    default:
      return {
        operator: condition.operator as GlobalOperator,
        right: { type: 'Literal', value: condition.value },
      };
  }
};
export const convertJsonRulesEngineToAst = (
  json: RuleTopLevelCondition | ConditionProperties
): TopLevelSchema | ConditionSchema => {
  if (isCondition(json)) {
    // Generic operators
    const operator = json.operator.replace(
      /^Generic\./,
      ''
    ) as UtilSchema<GenericMethodsSchema>;
    if (
      json.operator === 'Generic.isNilOrEmpty' ||
      json.operator === 'Generic.isNotNilOrEmpty'
    ) {
      return {
        type: 'CallExpression',
        callee: {
          type: 'MemberExpression',
          object: {
            type: 'Identifier',
            name: 'Generic',
          },
          property: {
            type: 'Identifier',
            name: operator,
          },
        },
        arguments: [
          {
            type: 'CallExpression',
            callee: { type: 'Identifier', name: 'value' },
            arguments: [{ type: 'Literal', value: json.fact }],
          },
        ],
      };
    }

    // String operators
    if (
      json.operator === 'String.in' ||
      json.operator === 'String.notIn' ||
      json.operator === 'String.includes' ||
      json.operator === 'String.notIncludes' ||
      json.operator === 'String.startsWith'
    ) {
      let args: ExpressionSimpleType[];
      if (json.operator === 'String.in' || json.operator === 'String.notIn') {
        args = json.value;
      } else {
        args = [json.value];
      }
      const operator = json.operator.replace(
        /^String\./,
        ''
      ) as UtilSchema<StringMethodsSchema>;
      return {
        type: 'CallExpression',
        callee: {
          type: 'MemberExpression',
          object: {
            type: 'Identifier',
            name: 'String',
          },
          property: {
            type: 'Identifier',
            name: operator,
          },
        },
        arguments: [
          {
            type: 'CallExpression',
            callee: { type: 'Identifier', name: 'value' },
            arguments: [{ type: 'Literal', value: json.fact }],
          },
          ...args.map((x) => ({
            type: 'Literal' as const, // fixme: move Literal and other types to enum
            value: x,
          })),
        ],
      };
    }

    // Date operators
    if (
      json.operator === 'Date.isAfter' ||
      json.operator === 'Date.isBefore' ||
      json.operator === 'Date.willHappenWithinDays' ||
      json.operator === 'Date.didHappenWithinDays'
    ) {
      const operator = json.operator.replace(
        /^Date\./,
        ''
      ) as UtilSchema<DateMethodsSchema>;
      const arg = json.value;
      return {
        type: 'CallExpression',
        callee: {
          type: 'MemberExpression',
          object: {
            type: 'Identifier',
            name: 'Date',
          },
          property: {
            type: 'Identifier',
            name: operator,
          },
        },
        arguments: [
          {
            type: 'CallExpression',
            callee: { type: 'Identifier', name: 'value' },
            arguments: [{ type: 'Literal', value: json.fact }],
          },
          {
            type: 'Literal',
            value: arg,
          },
        ],
      };
    }

    const operatorAndValue =
      getExpressionConditionFromJsonRulesEngineCondition(json);
    return {
      type: 'BinaryExpression',
      left: {
        type: 'CallExpression',
        callee: { type: 'Identifier', name: 'value' },
        arguments: [
          {
            type: 'Literal',
            value: json.fact,
          },
        ],
      },
      ...operatorAndValue,
    } as BinaryConditionSchema;
  }
  const isAll = isAndGroup(json);
  let group;
  if (isAndGroup(json)) {
    group = json.all;
  } else {
    group = json.any;
  }
  return {
    type: 'CallExpression',
    callee: {
      type: 'MemberExpression',
      object: { type: 'Identifier', name: 'Boolean' },
      property: { type: 'Identifier', name: isAll ? 'and' : 'or' },
    },
    arguments: group.map((x) =>
      convertJsonRulesEngineToAst(x as ConditionProperties)
    ),
  } as TopLevelSchema;
};

export type RuleWithTree = CreateRuleDto & {
  audience: RuleTopLevelCondition;
};
type EditRuleContextProps = {
  // Rule
  rule: RuleWithTree;
  setRule: React.Dispatch<React.SetStateAction<RuleWithTree>>;

  // Properties
  properties?: AudienceFact[];

  // Misc
  isPristine: boolean;
  reset: () => void;

  // Async functions
  save: () => Promise<Rule>;
};
export const EditRuleContext = createContext<EditRuleContextProps>(
  {} as EditRuleContextProps
);

type EditRuleProviderProps = {
  rule: RuleWithTree & { id?: string };
  properties?: AudienceFact[];
};
export const EditRuleProvider: React.FC<
  React.PropsWithChildren<EditRuleProviderProps>
> = ({ properties, rule: initRule, children }) => {
  const appKey = useSelector(selectAppId);
  const [createRule] = rulesSlice.endpoints.createRule.useMutation();
  const [updateRule] = rulesSlice.endpoints.updateRule.useMutation();

  const [isPristine, setIsPristine] = useState(true);

  // Rule
  const [rule, setRuleState] = useState(initRule);
  const setRule: typeof setRuleState = (...args) => {
    setIsPristine(false);
    setRuleState(...args);
  };

  // Misc
  const reset = () => {
    setIsPristine(true);
    setRuleState(initRule);
  };

  // Async functions
  const save = () => {
    const ast = convertJsonRulesEngineToAst(rule.audience);
    const code = BrightbackExpression.astToString(ast as jsep.Expression);
    // console.log('save, ast', ast);
    // console.log('save, code', code);

    let promise: Promise<Rule>;
    if (rule.id === undefined) {
      const newRule = {
        ..._.pick(rule, ['display_name']),
        client_formatted_rule: code,
        active: true,
      };

      promise = createRule({ appKey, ...newRule })
        .unwrap()
        .catch((err: RTKAPIErrorResponse) => {
          throw err.data;
        });
    } else {
      const updatedRule = {
        id: rule.id,
        ..._.pick(rule, ['display_name', 'active']),
        client_formatted_rule: code,
      };

      promise = updateRule({
        appKey,
        comment: 'updated',
        ...updatedRule,
      })
        .unwrap()
        .catch((err: RTKAPIErrorResponse) => {
          throw err.data;
        });
    }

    return promise;
  };

  return (
    <EditRuleContext.Provider
      value={{
        // Rule
        rule,
        setRule,

        // Properties
        properties,

        // Misc
        isPristine,
        reset,

        // Async functions
        save,
      }}
    >
      {children}
    </EditRuleContext.Provider>
  );
};
